import { Workbox } from 'workbox-window';
import { Parallel } from '../../modules/Parallel.js';
import { lazyFeatureFiles, externalAssetUrls } from './ServiceWorkerCacheWarmingConfig.js';

/** @import { WorkboxLifecycleWaitingEvent, WorkboxLifecycleEvent } from 'workbox-window' */

const MANUAL_UPDATE_INTERVAL = 1000 * 60 * 60 * 12; // 12 hours

/**
 * Initialize the service worker for the mobile app. Adapted from {@link https://developer.chrome.com/docs/workbox/handling-service-worker-updates#the_code_to_put_in_your_page | Workbox's documentation}.
 * @param {Object} options - Options for the service worker.
 * @param {(event: WorkboxLifecycleWaitingEvent) => void} options.onUpdateReady - A function called when a new service worker is available.
 * It should return a promise that resolves to true if the user wants to update (and false if not).
 * @param {function} [options.onOfflineReady] - A function called when the service worker is installed. Use
 * this to notify the user that the app is ready to be used offline.
 * @returns {{ updateAndReload: () => void }} A function that can be called to update the service worker and reload the page. Does nothing
 * if no update is available.
 */
export function initializeServiceWorker({ onUpdateReady, onOfflineReady }) {
  // Make sure onUpdateReady is provided
  if (typeof onUpdateReady != 'function') throw new Error('options.onUpdateReady is required to initialize the service worker');

  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.error('Service worker could not be initialized: Service workers are not supported in this browser');
    return { updateAndReload: () => console.error('User attempted to update service worker, but service workers are not supported') };
  }

  const wb = new Workbox('./sw.js');
  let wbIsWaiting = false;

  // Add an event listener to detect when the service worker has been updated
  wb.addEventListener('waiting', (event) => {
    wbIsWaiting = true;
    onUpdateReady(event);
  });
  // If the service worker was waiting but is now controlling, reload the page
  wb.addEventListener('controlling', () => {
    if (!wbIsWaiting) return;

    // At this point, reloading will ensure that the current tab is loaded under the control of the new service worker.
    // You may want to auto-save or persist transient state before triggering the reload.
    window.location.reload();
  });

  // Set offline is ready when the service worker is activated for the first time
  wb.addEventListener('activated', async (event) => {
    if (event.isUpdate) return;

    await warmMobileCache().catch((err) => {
      console.error('Failed to warm mobile cache:', err);
    });

    // When the service worker is activated for the first time, it is ready to handle offline requests
    onOfflineReady?.();
  });

  wb.register();

  // Usually updates are only checked when the page is loaded; we want to manually check for updates on interval
  setInterval(() => wb.update(), MANUAL_UPDATE_INTERVAL);

  // This can be called to update the service worker and reload the page.
  const updateAndReload = () => {
    // Don't do anything if there's no update available.
    if (!wbIsWaiting) return;

    // Message the service worker to skip the waiting and activate the new service worker immediately.
    wb.messageSkipWaiting();
  };

  return { updateAndReload };
}

// These files are kept out of the precache so the service worker doesn't time out while installing,
// especially on iOS. Instead we warm them into the cache from the page once the worker has activated.
// The feature files are stored as bare names in {@link ./ServiceWorkerCacheWarmingConfig.js}, so we
// turn each into a full URL based on this module's location before fetching.
const urlsToWarmCache = [...lazyFeatureFiles.map((file) => new URL(file, import.meta.url).href), ...externalAssetUrls];

async function warmMobileCache() {
  await Parallel.forEach(
    urlsToWarmCache,
    async (path) => {
      try {
        const res = await fetch(path);
        if (!res.ok) {
          console.error('Failed to warm cache for path:', path, res.statusText);
        }
      } catch (err) {
        console.error('Failed to warm cache for path:', path, err);
      }
    },
    8
  );
}
